import { supabase } from "../lib/supabaseClient";

class AnalyticsService {
  private static VISITOR_KEY = "fl_visited_home";
  private static VIEWED_POSTS_KEY = "fl_viewed_posts_map";
  private processingViews = new Set<string>();

  /**
   * 세션에서 조회된 게시글 맵 가져오기
   */
  private getViewedPostsMap(): Record<string, number[]> {
    const stored = sessionStorage.getItem(AnalyticsService.VIEWED_POSTS_KEY);
    if (!stored) return { sermon: [], column: [] };
    try {
      return JSON.parse(stored);
    } catch {
      return { sermon: [], column: [] };
    }
  }

  /**
   * 세션에 조회된 게시글 기록하기
   */
  private markPostAsViewed(type: "sermon" | "column", id: number) {
    const map = this.getViewedPostsMap();
    if (!map[type]) map[type] = [];
    if (!map[type].includes(id)) {
      map[type].push(id);
      sessionStorage.setItem(
        AnalyticsService.VIEWED_POSTS_KEY,
        JSON.stringify(map),
      );
    }
  }

  /**
   * 특정 게시글이 이미 조회되었는지 확인
   */
  private isPostViewed(type: "sermon" | "column", id: number): boolean {
    const map = this.getViewedPostsMap();
    return map[type]?.includes(id) || false;
  }

  /**
   * 총 누적 방문자 수 증가 (세션당 1회)
   */
  async incrementTotalVisitorCount() {
    if (
      this.processingViews.has(AnalyticsService.VISITOR_KEY) ||
      sessionStorage.getItem(AnalyticsService.VISITOR_KEY)
    ) {
      return;
    }

    this.processingViews.add(AnalyticsService.VISITOR_KEY);
    // 즉시 세션 스토리지에 기록
    sessionStorage.setItem(AnalyticsService.VISITOR_KEY, "true");

    try {
      // 방문자 수 증가는 RPC만 사용합니다.
      const { error } = await supabase.rpc("increment_total_visitors");

      if (error) throw error;
    } catch (err) {
      console.error("Error incrementing visitor count:", err);
      sessionStorage.removeItem(AnalyticsService.VISITOR_KEY);
    } finally {
      this.processingViews.delete(AnalyticsService.VISITOR_KEY);
    }
  }

  /**
   * 총 누적 방문자 수 가져오기
   */
  async getTotalVisitorCount(): Promise<number> {
    try {
      const { data, error } = await supabase
        .from("site_stats")
        .select("total_visitors")
        .eq("id", 1)
        .single();

      if (error) throw error;
      return data.total_visitors || 0;
    } catch (err) {
      console.error("Error fetching total visitor count:", err);
      return 0;
    }
  }

  /**
   * 특정 게시글 조회수 증가 (세션당 1회)
   * @returns 조회가 실제로 기록되었는지 여부 (이미 조회한 경우 false)
   */
  async incrementViewCount(
    type: "sermon" | "column",
    id: number,
  ): Promise<boolean> {
    const procKey = `${type}_${id}`;

    // 이미 처리 중이거나 이미 세션에서 조회한 경우 패스
    if (this.processingViews.has(procKey) || this.isPostViewed(type, id)) {
      return false;
    }

    this.processingViews.add(procKey);
    // 즉시 세션 스토리지에 기록하여 다음 호출(Strict Mode 등)에서 패스하게 함
    this.markPostAsViewed(type, id);

    try {
      const rpcName =
        type === "sermon" ? "increment_sermon_view" : "increment_column_view";
      const { error } = await supabase.rpc(rpcName, { target_id: id });

      if (error) {
        console.warn(
          `RPC ${rpcName} failed, attempting fallback...`,
          error.message,
        );
        // RPC가 없는 경우 대비
        const table = type === "sermon" ? "sermons" : "columns";
        const { data: currentPost, error: fetchError } = await supabase
          .from(table)
          .select("view_count")
          .eq("id", id)
          .single();

        if (fetchError) {
          console.error(
            `Fallback fetch failed for ${table}:`,
            fetchError.message,
          );
          throw fetchError;
        }

        if (currentPost) {
          const { error: updateError } = await supabase
            .from(table)
            .update({ view_count: (currentPost.view_count || 0) + 1 })
            .eq("id", id);

          if (updateError) {
            console.error(
              `Fallback update failed for ${table}:`,
              updateError.message,
            );
            throw updateError;
          }
        }
      }
      return true;
    } catch (err) {
      console.error(`Error incrementing ${type} view count:`, err);
      // 에러 발생 시 다시 시도할 수 있도록 상태 롤백 (JSON 맵에서 제거)
      const map = this.getViewedPostsMap();
      if (map[type]) {
        map[type] = map[type].filter((vId) => vId !== id);
        sessionStorage.setItem(
          AnalyticsService.VIEWED_POSTS_KEY,
          JSON.stringify(map),
        );
      }
      return false;
    } finally {
      this.processingViews.delete(procKey);
    }
  }
}

export const analyticsService = new AnalyticsService();
